import React, { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import JsonLd from '../components/JsonLd';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import AdSlot from '../components/AdSlot';
import FAQAccordion from '../components/FAQAccordion';
import RelatedTools from '../components/RelatedTools';
import { calculateDailyHours, minutesToTime, formatCurrency, formatNumber } from '../utils/timeCalculations';
import { getCurrentMonthYear } from '../utils/currentDate';

const HorasTrabalhadasPage = () => {
  const [entrada, setEntrada] = useState('08:00');
  const [saida, setSaida] = useState('17:00');
  const [intervalo, setIntervalo] = useState(60);
  const [diasSemana, setDiasSemana] = useState({
    seg: true,
    ter: true,
    qua: true,
    qui: true,
    sex: true,
    sab: false,
    dom: false
  });
  const [valorHora, setValorHora] = useState('');

  const result = useMemo(() => {
    const daily = calculateDailyHours(entrada, saida, intervalo);
    const workingDays = Object.values(diasSemana).filter(d => d).length;
    const weeklyDecimal = daily.decimal * workingDays;
    const monthlyDecimal = weeklyDecimal * 4.35; // Average weeks per month

    let salarioData = null;
    if (valorHora && parseFloat(valorHora) > 0) {
      const valor = parseFloat(valorHora);
      salarioData = {
        semanal: weeklyDecimal * valor,
        mensal: monthlyDecimal * valor
      };
    }

    return {
      daily,
      weeklyDecimal,
      monthlyDecimal,
      workingDays,
      salarioData
    };
  }, [entrada, saida, intervalo, diasSemana, valorHora]);

  const toggleDia = (dia) => {
    setDiasSemana({ ...diasSemana, [dia]: !diasSemana[dia] });
  };

  const faqs = [
    {
      question: 'Quantas horas posso trabalhar por dia pela CLT?',
      answer: 'A jornada padrão da CLT é de até 8 horas diárias e 44 horas semanais (Art. 58). Com horas extras, o máximo permitido é 10 horas diárias (8 normais + 2 extras). Em regime 12x36, a jornada pode chegar a 12 horas, seguida de 36 horas de descanso.'
    },
    {
      question: 'Como calcular horas trabalhadas com intervalo?',
      answer: 'Subtraia o horário de entrada do horário de saída e depois desconte o intervalo. Exemplo: entrada 08:00, saída 17:30, intervalo 1h. Cálculo: 17:30 - 08:00 = 9h30min. Menos 1h de intervalo = 8h30min trabalhadas. Em decimal: 8,50 horas.'
    },
    {
      question: 'Qual a jornada semanal máxima permitida?',
      answer: 'A jornada máxima é de 44 horas semanais para a maioria dos trabalhadores CLT. Isso pode ser distribuído em 5 ou 6 dias. Com horas extras, o limite diário é de 10 horas, mas as horas extras devem ser pagas com adicional de 50% ou compensadas via banco de horas.'
    },
    {
      question: 'Como converter horas trabalhadas em decimal?',
      answer: 'Divida os minutos por 60. Exemplo: 8h45min = 8 + (45/60) = 8,75 horas. Para converter de volta: 8,75h = 8 horas + (0,75 x 60) = 8h45min. O formato decimal é utilizado pela maioria dos sistemas de folha de pagamento.'
    }
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <>
      <Helmet>
        <html lang="pt-BR" />
        <title>Calculadora de Horas Trabalhadas Online — Semanal e Mensal</title>
        <meta name="description" content="Calcule horas trabalhadas por dia, semana e mês conforme a CLT. Entrada, saída e intervalo. Resultado em HH:MM, decimal e valor em R$. Atualizada 2026." />
        <meta name="robots" content="index, follow, max-snippet:-1, max-video-preview:-1, max-image-preview:large" />
        <link rel="canonical" href="https://calculadoradehoras.net.br/calculadora-de-horas-trabalhadas" />
        
        {/* Open Graph Tags */}
        <meta property="og:title" content="Calculadora de Horas Trabalhadas Online — Semanal e Mensal" />
        <meta property="og:description" content="Calcule horas trabalhadas por dia, semana e mês conforme a CLT. Entrada, saída e intervalo. Resultado em HH:MM, decimal e valor em R$. Atualizada 2026." />
        <meta property="og:url" content="https://calculadoradehoras.net.br/calculadora-de-horas-trabalhadas" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="pt_BR" />
        <meta property="og:site_name" content="Calculadora de Horas" />
        <meta property="og:image" content="https://calculadoradehoras.net.br/og-image.png" />
        
        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Calculadora de Horas Trabalhadas Online — Semanal e Mensal" />
        <meta name="twitter:description" content="Calcule horas trabalhadas por dia, semana e mês conforme a CLT. Entrada, saída e intervalo. Resultado em HH:MM, decimal e valor em R$. Atualizada 2026." />
        
        <link rel="alternate" hrefLang="pt-BR" href="https://calculadoradehoras.net.br/calculadora-de-horas-trabalhadas" />
        <link rel="alternate" hrefLang="x-default" href="https://calculadoradehoras.net.br/calculadora-de-horas-trabalhadas" />
      </Helmet>
      <JsonLd data={structuredData} />
      <JsonLd data={{
        "@context": "https://schema.org", "@type": "WebApplication",
        "name": "Calculadora de Horas Trabalhadas",
        "url": "https://calculadoradehoras.net.br/calculadora-de-horas-trabalhadas",
        "description": "Calcule horas trabalhadas por dia, semana e mês conforme a CLT. Resultado em HH:MM, decimal e R$.",
        "applicationCategory": "UtilityApplication", "operatingSystem": "All",
        "offers": { "@type": "Offer", "price": "0", "priceCurrency": "BRL" }, "inLanguage": "pt-BR"
      }} />
      <JsonLd data={{
        "@context": "https://schema.org", "@type": "BreadcrumbList",
        "itemListElement": [
          {"@type":"ListItem","position":1,"name":"Início","item":"https://calculadoradehoras.net.br/"},
          {"@type":"ListItem","position":2,"name":"Horas Trabalhadas","item":"https://calculadoradehoras.net.br/calculadora-de-horas-trabalhadas"}
        ]
      }} />

      <Navigation />
      
      <main className="max-w-4xl mx-auto px-4 py-8">
        <nav className="text-sm text-body-text mb-4">
          <a href="/" className="hover:text-blue-primary">Início</a>
          <span className="mx-2">›</span>
          <span>Horas Trabalhadas</span>
        </nav>
        <div className="text-center mb-8">
          <h1 className="text-4xl md:text-5xl font-heading font-bold text-dark-text mb-4">
            Calculadora de Horas Trabalhadas — Semanal e Mensal
          </h1>
          <p className="text-lg text-body-text">
            Calcule suas horas trabalhadas por dia, semana e mês conforme a CLT
          </p>
          <p className="text-sm text-body-text mt-2" data-testid="last-updated" suppressHydrationWarning>{`Última atualização: ${getCurrentMonthYear()}`}</p>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-8">
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-dark-text mb-2">
                  Horário de Entrada
                </label>
                <input
                  type="time"
                  value={entrada}
                  onChange={(e) => setEntrada(e.target.value)}
                  className="w-full h-[52px] px-4 border-2 border-border-gray rounded-lg font-mono text-lg focus:outline-none focus:border-blue-primary focus:ring-2 focus:ring-blue-primary"
                  data-testid="entrada-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-dark-text mb-2">
                  Horário de Saída
                </label>
                <input
                  type="time"
                  value={saida}
                  onChange={(e) => setSaida(e.target.value)}
                  className="w-full h-[52px] px-4 border-2 border-border-gray rounded-lg font-mono text-lg focus:outline-none focus:border-blue-primary focus:ring-2 focus:ring-blue-primary"
                  data-testid="saida-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-dark-text mb-2">
                Intervalo (minutos)
              </label>
              <input
                type="number"
                min="0"
                value={intervalo}
                onChange={(e) => setIntervalo(parseInt(e.target.value) || 0)}
                className="w-full h-[52px] px-4 border-2 border-border-gray rounded-lg font-mono text-lg focus:outline-none focus:border-blue-primary focus:ring-2 focus:ring-blue-primary"
                data-testid="intervalo-input"
              />
              <p className="mt-1 text-sm text-body-text">
                Padrão: 60 minutos (1 hora) para jornadas acima de 6 horas
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-dark-text mb-3">
                Dias da Semana
              </label>
              <div className="grid grid-cols-4 md:grid-cols-7 gap-2">
                {[
                  { key: 'seg', label: 'Seg' },
                  { key: 'ter', label: 'Ter' },
                  { key: 'qua', label: 'Qua' },
                  { key: 'qui', label: 'Qui' },
                  { key: 'sex', label: 'Sex' },
                  { key: 'sab', label: 'Sáb' },
                  { key: 'dom', label: 'Dom' }
                ].map(dia => (
                  <button
                    key={dia.key}
                    onClick={() => toggleDia(dia.key)}
                    className={`h-12 rounded-lg font-medium transition-colors ${
                      diasSemana[dia.key]
                        ? 'bg-blue-primary text-white'
                        : 'bg-gray-100 text-body-text hover:bg-gray-200'
                    }`}
                    data-testid={`dia-${dia.key}`}
                  >
                    {dia.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-dark-text mb-2">
                Valor da Hora (R$) - Opcional
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={valorHora}
                onChange={(e) => setValorHora(e.target.value)}
                placeholder="0.00"
                className="w-full h-[52px] px-4 border-2 border-border-gray rounded-lg font-mono text-lg focus:outline-none focus:border-blue-primary focus:ring-2 focus:ring-blue-primary"
                data-testid="valor-hora-input"
              />
            </div>
          </div>

          <div className="mt-8 space-y-4">
            <div className="bg-light-bg rounded-lg p-6">
              <p className="font-bold font-heading font-semibold text-dark-text mb-4">Resumo</p>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-body-text">Horas por dia:</span>
                  <span className="font-mono font-bold text-dark-text">
                    {`${minutesToTime(result.daily.totalMinutes)} (${formatNumber(result.daily.decimal)}h)`}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-body-text">Dias trabalhados:</span>
                  <span className="font-mono font-bold text-dark-text">{`${result.workingDays} dias`}</span>
                </div>
                <div className="flex justify-between items-center border-t pt-3">
                  <span className="text-body-text">Horas por semana:</span>
                  <span className="font-mono font-bold text-blue-primary text-lg">
                    {`${formatNumber(result.weeklyDecimal)}h`}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-body-text">Horas por mês:</span>
                  <span className="font-mono font-bold text-blue-primary text-lg">
                    {`${formatNumber(result.monthlyDecimal)}h`}
                  </span>
                </div>
                {result.salarioData && (
                  <>
                    <div className="border-t pt-3" />
                    <div className="flex justify-between items-center">
                      <span className="text-body-text">Salário semanal:</span>
                      <span className="font-bold text-success-green text-lg">
                        {formatCurrency(result.salarioData.semanal)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-body-text">Salário mensal:</span>
                      <span className="font-bold text-success-green text-lg">
                        {formatCurrency(result.salarioData.mensal)}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        <AdSlot slotId="ad-slot-1" />

        <article className="prose prose-lg max-w-none">
          <section className="mb-12">
            <h2 className="text-3xl font-heading font-bold text-dark-text mb-4">Como Calcular Horas Trabalhadas</h2>
            <p className="text-body-text leading-relaxed mb-4">
              Calcular corretamente as horas trabalhadas é essencial para garantir que você receba o salário justo e para que a empresa cumpra as exigências da CLT. Nossa calculadora de horas trabalhadas automatiza todo o processo.
            </p>
            <p className="text-body-text leading-relaxed mb-4">
              Para usar a ferramenta, siga estas etapas:
            </p>
            <ol className="list-decimal list-inside space-y-2 text-body-text mb-4">
              <li>Insira o <strong>horário de entrada</strong> (por exemplo, 08:00).</li>
              <li>Insira o <strong>horário de saída</strong> (por exemplo, 17:00).</li>
              <li>Informe o <strong>tempo de intervalo</strong> em minutos (padrão: 60 minutos para jornadas acima de 6 horas, conforme Art. 71 da CLT).</li>
              <li>Selecione os <strong>dias da semana</strong> em que você trabalha (segunda a sexta é o padrão, mas você pode incluir sábado e domingo).</li>
              <li>Opcionalmente, insira o <strong>valor da sua hora</strong> em R$ para calcular o salário semanal e mensal.</li>
            </ol>
            <p className="text-body-text leading-relaxed">
              A calculadora exibe o resultado em horas por dia, por semana e por mês, tanto em formato HH:MM quanto em decimal. Se você informou o valor da hora, também mostra o salário bruto estimado.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-heading font-bold text-dark-text mb-4">Jornada de Trabalho na CLT: Art. 58 a 65</h2>
            <p className="text-body-text leading-relaxed mb-4">
              A <a href="https://www.planalto.gov.br/ccivil_03/decreto-lei/del5452.htm" target="_blank" rel="noopener noreferrer" className="text-blue-primary hover:underline">Consolidação das Leis do Trabalho (CLT)</a> estabelece regras claras sobre a jornada de trabalho no Brasil. Conhecer essas regras é fundamental para verificar se seus cálculos estão corretos.
            </p>
            <ul className="list-disc list-inside space-y-3 text-body-text mb-4">
              <li><strong>Jornada padrão (<a href="https://www.planalto.gov.br/ccivil_03/decreto-lei/del5452.htm#art58" target="_blank" rel="noopener noreferrer" className="text-blue-primary hover:underline">Art. 58</a>):</strong> A duração normal do trabalho é de até 8 horas diárias e 44 horas semanais. A maioria dos trabalhadores cumpre essa jornada de segunda a sexta (8 horas por dia) com 4 horas no sábado, ou distribui as 44 horas em 5 dias (8h48min por dia).</li>
              <li><strong>Intervalo para descanso (<a href="https://www.planalto.gov.br/ccivil_03/decreto-lei/del5452.htm#art71" target="_blank" rel="noopener noreferrer" className="text-blue-primary hover:underline">Art. 71</a>):</strong> Para jornadas acima de 6 horas, o intervalo mínimo é de 1 hora e máximo de 2 horas. Para jornadas entre 4 e 6 horas, o intervalo é de 15 minutos. Esse intervalo não é computado na jornada de trabalho.</li>
              <li><strong>Horas extras (<a href="https://www.planalto.gov.br/ccivil_03/decreto-lei/del5452.htm#art59" target="_blank" rel="noopener noreferrer" className="text-blue-primary hover:underline">Art. 59</a>):</strong> A jornada diária pode ser acrescida de até 2 horas extras, desde que haja acordo individual ou coletivo. A remuneração da hora extra deve ser pelo menos 50% superior à da hora normal.</li>
              <li><strong>Jornada parcial (Art. 58-A):</strong> Considera-se trabalho em regime de tempo parcial aquele cuja duração não exceda 30 horas semanais (sem hora extra) ou 26 horas semanais (com até 6 horas extras).</li>
            </ul>
            <p className="text-body-text leading-relaxed">
              Para mais informações sobre seus direitos trabalhistas e jurisprudência sobre jornada de trabalho, consulte o site do <a href="https://www.tst.jus.br/" target="_blank" rel="noopener noreferrer" className="text-blue-primary hover:underline">Tribunal Superior do Trabalho (TST)</a>.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-heading font-bold text-dark-text mb-4">Exemplos Práticos de Cálculo</h2>
            
            <div className="bg-light-bg p-6 rounded-lg mb-4">
              <h3 className="text-xl font-heading font-semibold text-dark-text mb-2">Exemplo 1: Jornada padrão CLT</h3>
              <p className="text-body-text leading-relaxed mb-2">
                Carlos trabalha de segunda a sexta, das 08:00 às 17:00, com 1 hora de almoço.
              </p>
              <p className="text-body-text leading-relaxed">
                <strong>Cálculo:</strong> 17:00 - 08:00 = 9 horas. Menos 1 hora de intervalo = <strong>8 horas trabalhadas por dia</strong>. Na semana: 8h x 5 dias = <strong>40 horas semanais</strong>. No mês (considerando 4,35 semanas): 40 x 4,35 = <strong>174 horas mensais</strong>.
              </p>
            </div>

            <div className="bg-light-bg p-6 rounded-lg mb-4">
              <h3 className="text-xl font-heading font-semibold text-dark-text mb-2">Exemplo 2: Jornada com sábado</h3>
              <p className="text-body-text leading-relaxed mb-2">
                Ana trabalha de segunda a sexta das 08:00 às 17:00 (8h por dia) e no sábado das 08:00 às 12:00 (4h).
              </p>
              <p className="text-body-text leading-relaxed">
                <strong>Cálculo semanal:</strong> (8h x 5) + 4h = <strong>44 horas semanais</strong> (jornada máxima CLT). No mês: 44 x 4,35 = <strong>191,4 horas mensais</strong>.
              </p>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-heading font-bold text-dark-text mb-4">Dicas Importantes</h2>
            <ul className="list-disc list-inside space-y-3 text-body-text">
              <li><strong>Divisor de horas:</strong> O divisor padrão para trabalhadores mensalistas com jornada de 44h semanais é <strong>220 horas</strong> (44 ÷ 6 × 30, incluindo o descanso semanal remunerado). Para jornada de 40h, o divisor é <strong>200 horas</strong> (40 ÷ 6 × 30).</li>
              <li><strong>Intervalo não computado:</strong> O horário de almoço não conta como hora trabalhada. Se você entra às 08:00 e sai às 17:00 com 1h de almoço, trabalhou 8 horas, não 9.</li>
              <li><strong>Sábado:</strong> Se sua empresa não trabalha aos sábados, as 4 horas podem ser distribuídas durante a semana, resultando em jornada de 8h48min de segunda a sexta.</li>
              <li><strong>Registro de ponto:</strong> A CLT exige registro de ponto para empresas com mais de 20 funcionários (Art. 74, §2). Mantenha seus próprios registros para conferência.</li>
            </ul>
          </section>
        </article>

        <AdSlot slotId="ad-slot-2" />

        <section className="my-12">
          <h2 className="text-3xl font-heading font-bold text-dark-text mb-6">Perguntas Frequentes</h2>
          <FAQAccordion faqs={faqs} />
        </section>

        <RelatedTools currentPath="/calculadora-de-horas-trabalhadas" />

        <AdSlot slotId="ad-slot-3" />
      </main>

      <Footer />
    </>
  );
};

export default HorasTrabalhadasPage;
